/**
 * @file: AttachmentPanel.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The WeChat-style "+" attachment grid revealed below the composer. Each
 * tile dispatches an {@link AttachmentAction}; the screen owns the actual
 * pick/upload/send behavior.
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  Camera,
  ClipboardList,
  FileBarChart,
  FileText,
  Image as ImageIcon,
  LucideIcon,
  Sparkles,
  User,
  Wrench,
} from 'lucide-react-native';
import { FC } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';

export type AttachmentAction =
  | 'media'
  | 'camera'
  | 'file'
  | 'contact'
  | 'order'
  | 'report'
  | 'service_record'
  | 'ai';

const ITEMS: Array<{
  key: AttachmentAction;
  label: string;
  Icon: LucideIcon;
  /** Greyed out and unpressable on native. */
  webOnly?: boolean;
}> = [
  { key: 'media', label: '照片/视频', Icon: ImageIcon },
  { key: 'camera', label: '拍摄', Icon: Camera },
  // Picking a document needs a DOM file input; native would need
  // expo-document-picker, which isn't a dependency — so the tile sits
  // disabled there rather than offering a tap that can only fail.
  { key: 'file', label: '文件', Icon: FileText, webOnly: true },
  { key: 'contact', label: '名片', Icon: User },
  { key: 'order', label: '工单', Icon: ClipboardList },
  { key: 'report', label: '检测报告', Icon: FileBarChart },
  { key: 'service_record', label: '服务记录', Icon: Wrench },
  { key: 'ai', label: 'AI 回复', Icon: Sparkles },
];

const isDisabled = (item: (typeof ITEMS)[number]) =>
  !!item.webOnly && Platform.OS !== 'web';

export const AttachmentPanel: FC<{
  onSelect: (action: AttachmentAction) => void;
}> = ({ onSelect }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingHorizontal: rpx(8),
        paddingTop: rpx(12),
        paddingBottom: rpx(4),
        backgroundColor: '#fafafa',
      }}
    >
      {ITEMS.map(item => {
        const { key, label, Icon } = item;
        const disabled = isDisabled(item);
        return (
          <View
            key={key}
            style={{
              width: '25%',
              alignItems: 'center',
              marginBottom: rpx(14),
            }}
          >
            <TouchableOpacity
              activeOpacity={0.7}
              disabled={disabled}
              onPress={() => onSelect(key)}
              style={{
                width: rpx(56),
                height: rpx(56),
                borderRadius: rpx(14),
                backgroundColor: disabled ? '#F5F7FA' : '#fff',
                borderWidth: 1,
                borderColor: '#E2E8F0',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={rpx(26)} color={disabled ? '#CBD5E1' : '#475569'} />
            </TouchableOpacity>
            <Typography.Text
              size={rpx(11)}
              color={disabled ? '#CBD5E1' : '#64748B'}
              style={{
                marginTop: rpx(6),
                fontFamily: FontFamily.NotoSans.Regular,
              }}
            >
              {label}
            </Typography.Text>
          </View>
        );
      })}
    </View>
  );
};
